import React, { FC, useState } from 'react'
import { Control } from 'react-hook-form'
import ControlledCheckbox from 'transfix-form/components/ControlledCheckbox'
import ControlledHiddenInput from 'transfix-form/components/ControlledHiddenInput'
import ControlledSelect from 'transfix-form/components/ControlledSelect'
import { InlineMessage } from 'transfix-ui/components/InlineMessage/InlineMessage'
import { INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage/constants'
import { Box, ClickAwayListener, Grid, Typography, Skeleton } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import {
  IMotorCarrierDetail,
  TEAM_MEMBER_ROLE_TYPES,
  TEAM_MEMBER_USER_TYPES,
} from 'apps/fleet-planner/types'
import { CarrierLabel } from './CarrierLabel'
import { CarrierTooltip } from './CarrierTooltip'

const USER_TYPE_OPTIONS = [
  {
    label: 'Dispatcher',
    value: TEAM_MEMBER_USER_TYPES.DISPATCHER,
  },
  {
    label: 'Owner Operator',
    value: TEAM_MEMBER_USER_TYPES.OWNER_OPERATOR,
  },
  {
    label: 'Driver',
    value: TEAM_MEMBER_USER_TYPES.DRIVER,
  },
]

const ROLE_OPTIONS = [
  {
    label: 'Admin',
    value: TEAM_MEMBER_ROLE_TYPES.ADMIN,
  },
  {
    label: 'Employee',
    value: TEAM_MEMBER_ROLE_TYPES.EMPLOYEE,
  },
]

export interface ICarrierListItemProps {
  /**
   * Index for the form array
   */
  idx: number
  /**
   * Motor Carrier identifier
   */
  motorCarrierId: IMotorCarrierDetail['motorCarrierId']
  /**
   * Form control
   */
  control: Control
  /**
   * The name of the carrier
   */
  legalName: string
  /**
   * Should the item show its selected state?
   */
  isItemSelected: boolean
  /**
   * Has access been removed from this carrier?
   */
  hasAccessBeenRemoved: boolean
  /**
   * Can the current user edit this item?
   */
  canEdit: boolean
}

export const CarrierListItem: FC<ICarrierListItemProps> = ({
  canEdit,
  control,
  hasAccessBeenRemoved,
  idx,
  isItemSelected,
  legalName,
  motorCarrierId,
}) => {
  const [isRoleTooltipOpen, setIsRoleTooltipOpen] = useState(false)
  const [isPermissionsTooltipOpen, setIsPermissionsTooltipOpen] = useState(false)

  const handleTooltipClose = () => {
    setIsRoleTooltipOpen(false)
    setIsPermissionsTooltipOpen(false)
  }

  const handleRoleTooltipOpen = () => {
    setIsPermissionsTooltipOpen(false)
    setIsRoleTooltipOpen(true)
  }

  const handlePermissionsTooltipOpen = () => {
    setIsRoleTooltipOpen(false)
    setIsPermissionsTooltipOpen(true)
  }

  return (
    <Box
      mt={1.5}
      p={2}
      pt={0}
      bgcolor={isItemSelected ? brandColors.skyBlue2 : brandColors.coolGray1}
      borderRadius='4px'
      minHeight={155}>
      <ControlledHiddenInput
        control={control}
        name={`motorCarrierDetails[${idx}].motorCarrierId`}
        defaultValue={motorCarrierId}
      />
      <ControlledHiddenInput
        control={control}
        name={`motorCarrierDetails[${idx}].legalName`}
        defaultValue={legalName}
      />
      <Box display='flex' alignItems='center' minHeight={54}>
        <ControlledCheckbox
          name={`motorCarrierDetails[${idx}].checked`}
          control={control}
          defaultValue={false}
          CheckboxProps={{
            label: <Typography variant='subtitle2'>{legalName}</Typography>,
            disabled: !canEdit,
          }}
        />
      </Box>
      {hasAccessBeenRemoved && (
        <InlineMessage
          message={
            <div>
              <strong>Note: </strong>
              <span>
                Your team member will no longer be able to view loads from this carrier and will
                have to be re-added if you change your mind.
              </span>
            </div>
          }
          type={INLINE_MESSAGE_VARIANTS.WARNING}
        />
      )}
      {!!isItemSelected && (
        <ClickAwayListener onClickAway={handleTooltipClose}>
          {/* Role tooltip */}
          <div>
            <CarrierTooltip onClose={handleTooltipClose} open={isRoleTooltipOpen}>
              <div>
                <h3>Dispatchers</h3>
                <ul>
                  <li>Can view and manage all of a carrier’s loads</li>
                  <li>Can only be assigned to loads as a Dispatcher</li>
                </ul>
                <h3>Drivers</h3>
                <ul>
                  <li>Can only view or manage loads they are assigned to</li>
                  <li>Can only be assigned to loads as a Driver</li>
                </ul>
                <h3>Owner Operators</h3>
                <ul>
                  <li>Can view and manage all of a carrier’s loads</li>
                  <li>Can be assigned to loads as a Dispatcher or Driver</li>
                </ul>
              </div>
            </CarrierTooltip>

            {/* Permissions tooltip */}
            <CarrierTooltip onClose={handleTooltipClose} open={isPermissionsTooltipOpen}>
              <div>
                <h3>Admin Permissions</h3>
                <ul>
                  <li>Can invite new team members to a carrier</li>
                  <li>Can view all of their carrier’s information</li>
                </ul>
                <h3>Employee Permissions</h3>
                <ul>
                  <li>Can’t invite new team members to a carrier</li>
                  <li>Can view all of their carrier’s information</li>
                </ul>
              </div>
            </CarrierTooltip>

            <Grid container spacing={2}>
              <Grid item xs={6} md={12}>
                <ControlledSelect
                  name={`motorCarrierDetails[${idx}].userType`}
                  control={control}
                  defaultValue={false}
                  SelectProps={{
                    dataTest: 'role-format',
                    label: <CarrierLabel text='Role' onClick={handleRoleTooltipOpen} />,
                    options: [...USER_TYPE_OPTIONS],
                    large: true,
                  }}
                />
              </Grid>
              <Grid item xs={6} md={12}>
                <ControlledSelect
                  name={`motorCarrierDetails[${idx}].role`}
                  control={control}
                  defaultValue={false}
                  SelectProps={{
                    dataTest: 'permissions-format',
                    label: (
                      <CarrierLabel text='Permissions' onClick={handlePermissionsTooltipOpen} />
                    ),
                    options: [...ROLE_OPTIONS],
                    large: true,
                  }}
                />
              </Grid>
            </Grid>
          </div>
        </ClickAwayListener>
      )}
    </Box>
  )
}

export const CarrierListItemSkeleton = React.memo(() => (
  <Box mt={1.5} px={2} bgcolor={brandColors.coolGray1} borderRadius='4px'>
    <Box display='flex' alignItems='center' minHeight={54}>
      <Box mr={1.5}>
        <Skeleton variant='rectangular' animation='wave' width={16} height={16} />
      </Box>
      <Skeleton variant='text' animation='wave' width={125} height={25} />
    </Box>
  </Box>
))

export const CarrierListItemEmpty = React.memo<{ message: string }>(({ message }) => (
  <Box mt={1.5} px={2} bgcolor={brandColors.coolGray1} borderRadius='4px'>
    <Box display='flex' alignItems='center' minHeight={54}>
      {message}
    </Box>
  </Box>
))
